import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import qs from 'qs-lite';

import api from '@src/utils/api';
import { VIDEO_COMPILATION } from '@src/constants/endpoints';
import { useVpSnackbar } from '@vp-ui';
import { getLibraryIdFromUrl } from '@src/helpers/videoCompilationHelper';

// start queries
const fetchVideoCompilations = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);

  return api.get(`${VIDEO_COMPILATION}${queryString ? `?${queryString}` : ''}`);
};
export const useFetchVideoCompilations = () => {
  const {
    limit = '25',
    offset = '0',
    orderBy = 'created_at',
    sortOrder = 'DESC',
  } = qs.parse(window?.location?.search?.split('?')?.[1]) || {};
  const queryProps = {
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  return useQuery([VIDEO_COMPILATION, { ...queryProps }], fetchVideoCompilations, {
    keepPreviousData: true,
  });
};

const getVideoCompilationId = (id) => {
  if (id) return id;
  const pathname = window?.location?.pathname || '';
  return getLibraryIdFromUrl(pathname);
};

const fetchVideoCompilationById = ({ queryKey }) => {
  const compilationId = queryKey[1];
  const isShared = queryKey[2];
  // The share param could be infered in the backend by analysing if url is public and contains isPreview param
  return api.get(`${VIDEO_COMPILATION}/${compilationId}?share=${isShared}&limit=250&offset=0`);
};

const fetchInfiniteVideoCompilationById = ({ queryKey, pageParam = 0 }) => {
  const compilationId = queryKey[1];
  const isShared = queryKey[2];
  const tags = queryKey[3];
  return api.get(
    `${VIDEO_COMPILATION}/${compilationId}?share=${isShared}&limit=12&offset=${pageParam}&tags=${tags}`
  );
};

export const useFetchVideoCompilationById = (props = {}) => {
  const { compilationId = '', isPublic = false, isPreview = false } = props;
  const compilationIdToUse = getVideoCompilationId(compilationId);

  return useQuery(
    [VIDEO_COMPILATION, compilationIdToUse, !isPreview && isPublic],
    fetchVideoCompilationById,
    {
      enabled: !!compilationIdToUse,
    }
  );
};

export const useInfiniteFetchVideoCompilationById = (props = {}) => {
  const { compilationId = '', isPublic = false, isPreview = false, tags = [] } = props;
  const compilationIdToUse = getVideoCompilationId(compilationId);

  return useInfiniteQuery(
    [VIDEO_COMPILATION, compilationIdToUse, !isPreview && isPublic, tags],
    fetchInfiniteVideoCompilationById,
    {
      getNextPageParam: (lastPage) =>
        lastPage.meta.offset + lastPage.meta.limit < lastPage.meta.count &&
        lastPage.meta.offset + lastPage.meta.limit,
    }
  );
};

const updateVideoCompilationPage = async ({
  data,
  id,
  useToast = true,
  navigationToast = false,
}) => {
  await api.put(`${VIDEO_COMPILATION}/${id}`, data);
  return { useToast, navigationToast };
};

export const useUpdateVideoCompilationPage = () => {
  const { addSnackbarSuccess, addSnackbarInfo } = useVpSnackbar();
  const queryClient = useQueryClient();

  return useMutation(updateVideoCompilationPage, {
    onSuccess: ({ useToast, navigationToast }) => {
      queryClient.invalidateQueries([VIDEO_COMPILATION]);
      useToast &&
        (navigationToast
          ? addSnackbarInfo({
            message: `Use the table below to select and deselect videos you'd like to include in your compilation. 
              Then hit Update Video Library to publish those changes!`,
            autoHideDuration: 15000,
          })
          : addSnackbarSuccess({ message: 'Successfully Updated Video Library' }));
    },
  });
};

const addMediaToCompilation = ({ compilationId, mediaId, burnInId }) =>
  api.post(`${VIDEO_COMPILATION}/${compilationId}/media`, { mediaId, burnInId: burnInId || null });

export const useAddMediaToLibrary = () => {
  const queryClient = useQueryClient();

  return useMutation(addMediaToCompilation, {
    onSuccess: () => {
      queryClient.invalidateQueries([VIDEO_COMPILATION]);
    }
  });
};

const postVideoCompilationPage = (payload) => api.post(`${VIDEO_COMPILATION}`, payload);

export const usePostVideoCompilationPage = () => {
  const { addSnackbarSuccess } = useVpSnackbar();
  const queryClient = useQueryClient();

  return useMutation(postVideoCompilationPage, {
    onSuccess: () => {
      queryClient.invalidateQueries([VIDEO_COMPILATION]);
      addSnackbarSuccess({ message: 'Successfully Created Video Library' });
    },
  });
};



// WEBPACK FOOTER //
// ./src/hooks/react-query/useVideoCompilationData.js